import React from 'react';
import { Formik, Form } from 'formik';
import { string, bool, func } from 'prop-types';
import { Modal, Button, Text, Stack, ButtonGroup } from '@chaser/components';
import axios from 'axios';
import usePush from '../../hooks/usePush';
import LabeledFieldWithError from '../LabeledFieldWithError';
import ChaserInput from '../ChaserInput';
import config from '../../config';
import { store } from '../../store';

const SentryFeedbackModal = ({ open, toggle, eventId }) => {
  const { execute, loading } = usePush(
    async values => {
      const { token } = store.getState().loginReducer;
      await axios.post(
        `${config.apiUrl}/sentry/feedback`,
        {
          name: values.name || 'Unknown',
          email: values.email || 'unknown@unknown.unknown',
          comments: values.comments,
          eventId,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    },
    {
      errorToast: 'An error occurred while submitting your report. Please try again.',
      successToast: 'Your feedback has been sent. Thank you!',
    },
  );

  return (
    <Modal title="It looks like we're having an issue" open={open} toggle={toggle}>
      <Formik
        initialValues={{
          name: '',
          email: '',
          comments: '',
        }}
        validate={values => {
          const errors = {};
          if (!values.comments) {
            errors.comments = 'Please tell us what happened';
          }
          return errors;
        }}
        validateOnMount
        onSubmit={async (values, actions) => {
          const [err] = await execute(values);
          actions.setSubmitting(false);
          if (err) return;
          toggle(false);
        }}
      >
        {({ isValid }) => (
          <Form>
            <Modal.Body>
              <Stack gap="medium">
                <Text>
                  Our team has been notified, but you can help us fix this issue quicker by
                  describing what happened.
                </Text>
                <Stack>
                  <LabeledFieldWithError
                    name="name"
                    as={ChaserInput}
                    label="Name (optional)"
                    width="full"
                  />
                  <LabeledFieldWithError
                    name="email"
                    type="email"
                    as={ChaserInput}
                    label="E-mail address (optional)"
                    width="full"
                  />
                  <LabeledFieldWithError
                    label="What happened?"
                    name="comments"
                    as={ChaserInput}
                    type="textarea"
                    placeholder="I was changing 'X' and then clicked on Submit"
                    width="full"
                  />
                </Stack>
              </Stack>
            </Modal.Body>
            <Modal.Footer>
              <ButtonGroup>
                <Button appearance="secondary" onClick={() => toggle(false)}>
                  Cancel
                </Button>
                <Button disabled={!isValid} loading={loading} type="submit">
                  Submit feedback
                </Button>
              </ButtonGroup>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

SentryFeedbackModal.propTypes = {
  open: bool.isRequired,
  toggle: func.isRequired,
  eventId: string.isRequired,
};

export default SentryFeedbackModal;
